import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Link2, ExternalLink, Edit, FolderOpen } from 'lucide-react';
import type { Link } from '@/types/legacy';
import { LinkFormDialog } from '@/components/Links/LinkFormDialog';

export default function Links() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<Link | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('all');

  const fetchLinks = async () => {
    try {
      const { data, error } = await api
        .from('links')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLinks(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los enlaces',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, []);
  useDataRefresh(fetchLinks, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredLinks = links.filter(link => {
    const matchesSearch =
      link.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.url?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesGroup = selectedGroup === 'all' || link.group_name === selectedGroup;

    return matchesSearch && matchesGroup;
  });

  // Get unique groups
  const groups = Array.from(new Set(links.map(link => link.group_name).filter(Boolean))) as string[];

  // Group links by group_name
  const groupedLinks = filteredLinks.reduce((acc, link) => {
    const group = link.group_name || 'Sin categoría';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(link);
    return acc;
  }, {} as Record<string, Link[]>);

  const handleEdit = (link: Link) => {
    setSelectedLink(link);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedLink(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Enlaces Útiles
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestión de enlaces y recursos externos
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Enlace
            </Button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar enlaces..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {groups.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedGroup === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedGroup('all')}
              >
                Todos
              </Button>
              {groups.map(group => (
                <Button
                  key={group}
                  variant={selectedGroup === group ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedGroup(group)}
                >
                  {group}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Enlaces</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {links.length}
                </p>
              </div>
              <Link2 className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-3xl font-bold text-success">
                  {links.filter(l => l.is_active).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Categorías</p>
                <p className="text-3xl font-bold text-primary">
                  {groups.length}
                </p>
              </div>
              <FolderOpen className="w-8 h-8 text-primary/50" />
            </div>
          </Card>
        </div>

        {/* Links List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredLinks.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Link2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron enlaces' : 'No hay enlaces creados'}
            </p>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedLinks).map(([groupName, groupLinks]) => (
              <div key={groupName}>
                <div className="flex items-center gap-3 mb-4">
                  <FolderOpen className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">{groupName}</h2>
                  <Badge variant="secondary">{groupLinks.length}</Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupLinks.map((link) => (
                    <Card key={link.id} className="glass-card p-4 hover:shadow-magma transition-all">
                      <div className="flex items-start gap-3">
                        {link.icon_url ? (
                          <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                            <img
                              src={link.icon_url}
                              alt={link.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Link2 className="w-6 h-6 text-primary" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="font-semibold truncate">{link.name}</h3>
                            {!link.is_active && (
                              <Badge variant="secondary" className="text-xs">
                                Inactivo
                              </Badge>
                            )}
                          </div>

                          {link.description && (
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {link.description}
                            </p>
                          )}

                          <div className="flex items-center gap-2">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-primary hover:underline flex-1 truncate"
                            >
                              <ExternalLink className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{link.url}</span>
                            </a>
                          </div>

                          {canManage && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(link)}
                                className="w-full"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <LinkFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        link={selectedLink}
        onSuccess={fetchLinks}
      />
    </DashboardLayout>
  );
}
